/* eslint-disable max-lines-per-function */
/**
 * Custom Subtitle Overlay Renderer
 *
 * Renders VTT subtitles as DOM elements instead of using native TextTrack.
 * This gives us full control over styling, particularly line-height, which
 * Chrome's ::cue pseudo-element ignores.
 *
 * Why custom rendering:
 * - Chrome ignores line-height CSS on ::cue (known limitation)
 * - For foreign language streaming, subtitle quality is critical
 * - Extracts parsed cues from HLS.js-loaded TextTracks (no duplicate parsing)
 *
 * Architecture:
 * - HLS.js loads and parses VTT files into video.textTracks
 * - player-core sets track.mode = "hidden" to load cues without native rendering
 * - This component extracts cues from TextTrack and renders as DOM elements
 */

import type {
  BackgroundOpacity,
  FontFamily,
  FontSize,
  SubtitlePosition,
  TextColor,
  TextEdgeStyle,
} from "@lumi/player-core";
import type { ReactElement } from "react";
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Global styles for WebVTT formatting tags
 * Ensures consistent rendering across browsers
 */
const VTT_TAG_STYLES = `
.custom-subtitle-text i { font-style: italic; }
.custom-subtitle-text b { font-weight: bold; }
.custom-subtitle-text u { text-decoration: underline; }
.custom-subtitle-text c { color: inherit; }
.custom-subtitle-text v { font-style: italic; opacity: 0.9; }
.custom-subtitle-text ruby { ruby-position: over; }
.custom-subtitle-text rt { font-size: 0.6em; }
`;

/**
 * Font size mapping from setting to CSS rem values
 */
const FONT_SIZE_MAP: Record<FontSize, string> = {
  small: "1.4rem",
  medium: "1.8rem",
  large: "2.2rem",
  "x-large": "2.6rem",
};

/**
 * Text color mapping from setting to CSS color values
 */
const TEXT_COLOR_MAP: Record<TextColor, string> = {
  white: "#ffffff",
  yellow: "#F3B33B",
  gray: "#808080",
  black: "#000000",
};

/**
 * Font family mapping from setting to CSS font-family values
 */
const FONT_FAMILY_MAP: Record<FontFamily, string> = {
  "proportional-sans": "system-ui, -apple-system, sans-serif",
  "monospace-sans": '"Courier New", Courier, monospace',
  "proportional-serif": 'Georgia, "Times New Roman", serif',
  "monospace-serif": '"Courier New", Courier, monospace',
};

/**
 * Text edge style mapping from setting to CSS text-shadow values
 */
const TEXT_EDGE_MAP: Record<TextEdgeStyle, string> = {
  none: "none",
  "drop-shadow": "2px 2px 1px rgba(0, 0, 0, 1)",
  "drop-shadow-strong": "3px 3px 6px rgba(0, 0, 0, 1), 0 0 8px rgba(0, 0, 0, 0.8)",
  uniform:
    "0 0 4px rgba(0, 0, 0, 1), 0 0 8px rgba(0, 0, 0, 0.8), 1px 1px 2px rgba(0, 0, 0, 0.9), -1px -1px 2px rgba(0, 0, 0, 0.9)",
};

/**
 * Parsed VTT subtitle cue
 */
type SubtitleCue = {
  index: number;
  startTime: number; // seconds
  endTime: number; // seconds
  text: string;
  // VTT cue settings (extracted from VTTCue)
  line?: number; // Vertical position (negative = from bottom)
  position?: number; // Horizontal position (percentage)
  size?: number; // Width (percentage)
  align?: "start" | "center" | "end";
};

/**
 * Convert a TextTrackCue to our SubtitleCue format
 * Extracts VTTCue-specific properties like positioning
 */
function convertCueToSubtitleCue(cue: TextTrackCue, index: number): SubtitleCue {
  const vttCue = cue as VTTCue;
  return {
    index,
    startTime: cue.startTime,
    endTime: cue.endTime,
    text: vttCue.text || "",
    line: vttCue.line === "auto" ? undefined : vttCue.line,
    position: vttCue.position === "auto" ? undefined : vttCue.position,
    size: vttCue.size,
    align: vttCue.align as "start" | "center" | "end" | undefined,
  };
}

/**
 * Extract all cues from a TextTrack and convert to SubtitleCue array
 * Returns empty array if track has no cues
 */
function extractCuesFromTrack(track: TextTrack | null): SubtitleCue[] {
  if (!track?.cues || track.cues.length === 0) {
    return [];
  }

  const parsed: SubtitleCue[] = [];
  for (let i = 0; i < track.cues.length; i++) {
    const cue = track.cues[i];
    if (cue) {
      parsed.push(convertCueToSubtitleCue(cue, i));
    }
  }
  return parsed;
}

export type CustomSubtitleOverlayProps = Readonly<{
  /** Video element reference to access TextTrack cues */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Current video playback time in seconds */
  currentTime: number;
  /** Whether subtitles are enabled */
  enabled: boolean;
  /** Active caption track index (-1 for off) */
  activeCaption: number | "off";
  /** Whether controls are visible (affects subtitle positioning) */
  controlsVisible?: boolean;
  /** User's preferred subtitle position setting */
  subtitlePosition?: SubtitlePosition;
  /** User's preferred subtitle font size */
  fontSize?: FontSize;
  /** User's preferred subtitle background opacity (0-100) */
  backgroundOpacity?: BackgroundOpacity;
  /** User's preferred subtitle text color */
  textColor?: TextColor;
  /** User's preferred subtitle font family */
  fontFamily?: FontFamily;
  /** User's preferred subtitle text edge style */
  textEdge?: TextEdgeStyle;
}>;

/**
 * Custom Subtitle Overlay Component
 *
 * Renders subtitles as DOM elements with proper line-height control.
 * Extracts cues from HLS.js-created TextTrack instead of fetching VTT files.
 */
export const CustomSubtitleOverlay = React.memo(function CustomSubtitleOverlay({
  videoRef,
  currentTime,
  enabled,
  activeCaption,
  controlsVisible = false,
  subtitlePosition = "auto",
  fontSize = "medium",
  backgroundOpacity = 50,
  textColor = "white",
  fontFamily = "proportional-sans",
  textEdge = "drop-shadow",
}: CustomSubtitleOverlayProps): ReactElement | null {
  const [cues, setCues] = useState<SubtitleCue[]>([]);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Extract cues from video TextTrack when caption changes or cues load
  useEffect(() => {
    if (!enabled || activeCaption === "off" || activeCaption < 0 || !videoRef.current) {
      setCues([]);
      return;
    }

    const video = videoRef.current;
    const textTracks = video.textTracks;

    // HLS tracks start at index 1 (index 0 is our dummy <track>)
    const hlsTrackIndex = activeCaption + 1;

    const tryExtractCues = (): boolean => {
      if (hlsTrackIndex >= textTracks.length) return false;

      const track = textTracks[hlsTrackIndex] ?? null;
      const parsed = extractCuesFromTrack(track);

      if (parsed.length > 0) {
        setCues(parsed);
        return true;
      }
      return false;
    };

    // Extract cues immediately if available
    const immediate = tryExtractCues();

    // Store refs for cleanup
    let pollInterval: NodeJS.Timeout | null = null;
    let trackedTrack: TextTrack | null = null;

    // Polling fallback: Check for cues every 100ms for up to 5 seconds
    // This handles async cue loading after track mode is set
    if (!immediate) {
      let pollAttempts = 0;
      const maxPollAttempts = 50; // 5 seconds (50 * 100ms)

      pollInterval = setInterval(() => {
        pollAttempts++;

        const success = tryExtractCues();

        if (success) {
          if (pollInterval) clearInterval(pollInterval);
          return;
        }

        // Stop polling after max attempts
        if (pollAttempts >= maxPollAttempts) {
          console.warn("⚠️ [CustomSubtitleOverlay] Subtitle cues failed to load after 5 seconds");
          if (pollInterval) clearInterval(pollInterval);
        }
      }, 100);
    }

    // Listen for TextTrack changes (tracks being added/removed or mode changes)
    const handleTrackChange = () => {
      tryExtractCues();
    };

    textTracks.addEventListener("change", handleTrackChange);
    textTracks.addEventListener("addtrack", handleTrackChange);

    // Also listen for individual track load and cuechange events
    if (hlsTrackIndex < textTracks.length) {
      const track = textTracks[hlsTrackIndex];
      if (track) {
        trackedTrack = track;
        track.addEventListener("load", handleTrackChange);
        track.addEventListener("cuechange", handleTrackChange);
      }
    }

    // Cleanup listeners
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      textTracks.removeEventListener("change", handleTrackChange);
      textTracks.removeEventListener("addtrack", handleTrackChange);
      if (trackedTrack) {
        trackedTrack.removeEventListener("load", handleTrackChange);
        trackedTrack.removeEventListener("cuechange", handleTrackChange);
      }
    };
  }, [enabled, activeCaption, videoRef]);

  // Find active cue for current time (memoized to prevent redundant calculations)
  const activeCue = useMemo((): SubtitleCue | null => {
    if (!cues.length) return null;
    return cues.find((c) => currentTime >= c.startTime && currentTime < c.endTime) || null;
  }, [cues, currentTime]);

  // Don't render if disabled or no active cue
  if (!enabled || !activeCue) {
    return null;
  }

  // Calculate positioning based on user's subtitle position preference
  let bottomPosition: string | undefined;
  let topPosition: string | undefined;

  // Base spacing when controls are visible: 6em (timeline + buttons)
  // Settings panel is absolutely positioned with its own z-index, so it doesn't affect subtitle position
  const baseSpacing = controlsVisible ? 6 : 0;

  if (subtitlePosition === "auto") {
    // Auto mode: respect VTT cue's original line position with control offset
    const line = activeCue.line ?? -2; // Default: 2 lines from bottom
    const isFromBottom = line <= 0;
    const lineOffset = Math.abs(line);

    bottomPosition = isFromBottom ? `${baseSpacing + lineOffset * 2}em` : undefined;
    topPosition = !isFromBottom ? `${lineOffset * 2}em` : undefined; // NOSONAR
  } else if (subtitlePosition === "top") {
    // Top position: fixed near top of video
    topPosition = "2em";
    bottomPosition = undefined;
  } else if (subtitlePosition === "middle") {
    // Middle position: centered vertically (50% with transform)
    topPosition = "50%";
    bottomPosition = undefined;
  } else if (subtitlePosition === "bottom") {
    // Bottom position: near bottom, accounting for controls
    bottomPosition = `${baseSpacing + 2}em`;
    topPosition = undefined;
  }

  const size = activeCue.size ?? 60; // Default: 60% width
  const align = activeCue.align ?? "center"; // Default: center aligned

  return (
    <div ref={overlayRef} className="custom-subtitle-overlay absolute inset-0 pointer-events-none z-40">
      {/* Inject VTT tag styles */}
      <style dangerouslySetInnerHTML={{ __html: VTT_TAG_STYLES }} />

      <div
        className="absolute transition-all duration-300 ease-out"
        style={{
          top: topPosition,
          bottom: bottomPosition,
          left: "50%",
          transform:
            subtitlePosition === "middle"
              ? "translate(-50%, -50%)" // Center vertically for middle position
              : "translateX(-50%)", // Only center horizontally otherwise
          width: `${size}%`,
          maxWidth: "100%",
          textAlign: align === "start" ? "left" : align === "end" ? "right" : "center", // NOSONAR
        }}
      >
        {/* Subtitle text with proper line-height */}
        <div
          className="custom-subtitle-text inline-block px-4 py-2 rounded"
          style={{
            backgroundColor: `rgba(0, 0, 0, ${backgroundOpacity / 100})`,
            color: TEXT_COLOR_MAP[textColor],
            fontSize: FONT_SIZE_MAP[fontSize],
            fontWeight: "600",
            lineHeight: "1.3",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: FONT_FAMILY_MAP[fontFamily],
            fontVariant: "normal",
            textShadow: TEXT_EDGE_MAP[textEdge],
          }}
          // Render VTT formatting tags as HTML
          dangerouslySetInnerHTML={{ __html: activeCue.text }}
        />
      </div>
    </div>
  );
});
